import React, { useEffect, useState } from 'react';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { rmisLookup, RmisCarrier, RmisLookupResult } from '../../services/rmis.service';

/**
 * RMIS lookup results (V1 main.pl rmislookup display). Queries RMIS by DOT or
 * MC/MX on open, lists the returned sections/fields, and — when RMIS mapped any
 * carrier fields — offers "Update Carrier" to copy them into the profile.
 */
interface Props {
  queryType: 'DOT' | 'MC_MX';
  queryId: string;
  onClose: () => void;
  onApply: (carrier: RmisCarrier) => void;
}

const RmisLookupModal: React.FC<Props> = ({ queryType, queryId, onClose, onApply }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RmisLookupResult | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    rmisLookup(queryType, queryId)
      .then((r) => { if (alive) setResult(r); })
      .catch((err: any) => { if (alive) setError(err?.response?.data?.detail || err?.message || 'RMIS lookup failed.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [queryType, queryId]);

  const hasCarrier = !!result && Object.keys(result.carrier || {}).length > 0;

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
        Close
      </button>
      {hasCarrier && (
        <button onClick={() => { onApply(result!.carrier); onClose(); }}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover inline-flex items-center gap-2">
          <ArrowDownTrayIcon className="w-4 h-4" /> Update Carrier
        </button>
      )}
    </div>
  );

  const label = queryType === 'DOT' ? 'DOT #' : 'MC/MX #';

  // Rendered only while open. Alt+C closes; applying the looked-up values into
  // the form isn't a persist, so it gets no Alt+S.
  useAltHotkeys({ C: onClose }, undefined, { layer: true });

  return (
    <Drawer isOpen onClose={onClose} title={`RMIS Lookup — ${label} ${queryId}`} position="right" width="34rem" footer={footer}>
      <div className="space-y-4">
        {loading && (
          <div className="flex items-center gap-3 text-sm text-muted">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            Looking up carrier in RMIS…
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {result && result.errors.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-3 text-sm text-amber-800 dark:text-amber-300">
            <p className="font-semibold">RMIS returned:</p>
            <ul className="list-disc list-inside">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        )}

        {result && !loading && result.sections.length === 0 && result.errors.length === 0 && (
          <p className="text-sm text-muted">No carrier information was returned.</p>
        )}

        {result?.sections.map((sec) => (
          <div key={sec.name} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/60 text-sm font-semibold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
              {sec.name}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {sec.fields.map((f, i) => (
                  <tr key={`${f.label}-${i}`} className="border-t border-gray-100 dark:border-gray-700/60 first:border-t-0">
                    <td className="py-1.5 px-3 text-muted w-2/5 align-top">{f.label}</td>
                    <td className="py-1.5 px-3 text-gray-800 dark:text-gray-200 break-words">{f.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </Drawer>
  );
};

export default RmisLookupModal;
